import React, { useState, useRef, useEffect, useCallback } from 'react';

interface CodeEntryProps {
  email: string;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  error?: string;
  attemptsRemaining?: number;
}

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const CodeEntry: React.FC<CodeEntryProps> = ({
  email,
  onSubmit,
  onResend,
  onBack,
  error,
  attemptsRemaining,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Start resend cooldown on mount
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const submitCode = useCallback(
    async (code: string) => {
      setLoading(true);
      try {
        await onSubmit(code);
      } finally {
        setLoading(false);
      }
    },
    [onSubmit],
  );

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    if (digit && index === CODE_LENGTH - 1) {
      const code = newDigits.join('');
      if (code.length === CODE_LENGTH) {
        submitCode(code);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        // Move back and clear previous
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
        inputRefs.current[index - 1]?.focus();
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === CODE_LENGTH) {
        submitCode(code);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    const newDigits = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);

    // Focus the next empty slot or last
    const nextEmpty = newDigits.findIndex((d) => !d);
    if (nextEmpty >= 0) {
      inputRefs.current[nextEmpty]?.focus();
    } else {
      inputRefs.current[CODE_LENGTH - 1]?.focus();
      // Auto-submit on full paste
      submitCode(newDigits.join(''));
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      await onResend();
      setResendCooldown(RESEND_COOLDOWN);
      // Clear digits for new code
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="card bg-base-200 shadow-md">
      <div className="card-body gap-4 text-left">
        <div className="text-center">
          <p className="text-sm text-base-content/60">
            We sent a verification code to
          </p>
          <p className="text-sm font-medium text-base-content mt-1">{email}</p>
        </div>

        {error && (
          <div className="alert alert-error text-sm">
            {error}
            {attemptsRemaining !== undefined && attemptsRemaining > 0 && (
              <span className="ml-1">
                ({attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''}{' '}
                remaining)
              </span>
            )}
          </div>
        )}

        <div className="flex justify-center gap-2" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={loading}
              className="input input-bordered w-12 h-14 text-center text-xl font-mono font-semibold focus:input-primary"
              aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
            />
          ))}
        </div>

        {loading && (
          <div className="flex justify-center">
            <span className="loading loading-spinner loading-sm text-primary" />
          </div>
        )}

        <div className="text-center text-sm text-base-content/60 mt-2">
          <p>
            Didn't receive the code?{' '}
            {resendCooldown > 0 ? (
              <span>Resend in {resendCooldown}s</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="link link-primary"
              >
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            )}
          </p>
        </div>

        <div className="divider my-0 text-xs text-base-content/40">or</div>

        <p className="text-center text-xs text-base-content/50">
          Click the link in your email to sign in instantly
        </p>

        <button
          type="button"
          onClick={onBack}
          className="btn btn-ghost btn-sm text-base-content/60"
        >
          Use a different email
        </button>
      </div>
    </div>
  );
};

export default CodeEntry;
